import React, { useEffect, useState } from "react";
import { Check, MapPin, Send, Sparkles, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";

const RED = "#e03131";
const RED_DARK = "#c92a2a";

const TYPES = [
  "Wildlife & Animal Care",
  "Turtle & Marine Conservation",
  "Teaching & Kindergarten",
  "Childcare & Special Needs",
  "Healthcare & Nutrition",
  "Medical Internship",
  "Community Construction",
  "Sports Coaching",
  "Environmental Conservation",
];

const LOCATIONS = [
  "Bali & Indonesia",
  "Fiji",
  "Thailand",
  "Vietnam",
  "Nepal",
  "Sri Lanka",
  "India",
  "Peru",
  "Ghana",
  "Kenya",
  "South Africa",
  "Cape Verde",
  "Not sure yet, surprise me",
];

const DURATIONS = ["1 to 2 weeks", "3 to 4 weeks", "5 to 8 weeks", "Longer than 8 weeks"];

export default function StartJourney() {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    type: "",
    location: "",
    duration: "",
    message: "",
  });

  // Slides in once the visitor has scrolled 15% of the page, then stays.
  useEffect(() => {
    const onScroll = () => {
      const d = document.documentElement;
      const max = d.scrollHeight - d.clientHeight;
      if (max > 0 && d.scrollTop / max >= 0.15) setVisible(true);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast({ title: "What should we call you?", description: "Please add your first name." });
      return;
    }
    if (!form.email.includes("@")) {
      toast({ title: "Check your email", description: "That address does not look right." });
      return;
    }
    if (!form.type || !form.location) {
      toast({
        title: "Almost there",
        description: "Pick a type of volunteering and a preferred location.",
      });
      return;
    }
    const saved = JSON.parse(localStorage.getItem("ivi_enquiries") || "[]");
    localStorage.setItem("ivi_enquiries", JSON.stringify([...saved, { ...form, at: Date.now() }]));
    setDone(true);
    toast({
      title: `Thanks ${form.name.trim()}!`,
      description: "One of our coordinators will be in touch within one business day.",
    });
  };

  const close = (v) => {
    setOpen(v);
    if (!v) {
      setDone(false);
      setForm({ name: "", email: "", type: "", location: "", duration: "", message: "" });
    }
  };

  return (
    <>
      {/* Desktop side tab: matte red */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-hidden={!visible}
        style={{
          backgroundImage:
            "linear-gradient(90deg, #e5453f 0%, #dc3a36 55%, #c92a2a 100%)",
          boxShadow:
            "inset 1px 0 0 rgba(255,255,255,0.16), inset -1px 0 0 rgba(0,0,0,0.14), -8px 6px 18px -10px rgba(140,26,26,0.45)",
        }}
        data-testid="start-journey-side-button"
        className={`group fixed right-0 top-1/2 z-40 hidden overflow-hidden rounded-l-2xl py-10 pl-[18px] pr-[16px] font-heading text-[17px] font-extrabold tracking-[0.03em] text-white transition-[transform,opacity,box-shadow,filter] duration-500 hover:brightness-105 lg:flex lg:items-center lg:justify-center ${
          visible
            ? "-translate-y-1/2 translate-x-0 opacity-100 hover:-translate-x-1"
            : "pointer-events-none -translate-y-1/2 translate-x-full opacity-0"
        }`}
      >
        <span className="relative whitespace-nowrap [writing-mode:vertical-rl] rotate-180">
          Start Your Journey
        </span>
      </button>

      {/* Mobile pill: matte red */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-hidden={!visible}
        style={{
          backgroundImage:
            "linear-gradient(180deg, #e5453f 0%, #dc3a36 55%, #c92a2a 100%)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.16), inset 0 -2px 0 rgba(0,0,0,0.14), 0 10px 20px -10px rgba(140,26,26,0.5)",
        }}
        className={`fixed bottom-5 left-1/2 z-40 inline-flex w-[86%] max-w-sm -translate-x-1/2 items-center justify-center gap-2.5 whitespace-nowrap rounded-full px-7 py-4 font-heading text-[16px] font-extrabold text-white transition-[transform,opacity] duration-500 lg:hidden ${
          visible
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-28 opacity-0"
        }`}
      >
        Start Your Journey
      </button>

      <Dialog open={open} onOpenChange={close}>
        <DialogContent
          aria-describedby={undefined}
          className="max-h-[92vh] max-w-4xl overflow-y-auto border-0 p-0 sm:rounded-[26px] [&>button:last-child]:hidden"
        >
          <DialogTitle className="sr-only">Start your volunteering journey</DialogTitle>

          <div className="grid md:grid-cols-[0.85fr_1fr]">
            {/* Photo side */}
            <div className="relative hidden min-h-[560px] md:block">
              <img
                src="/assets/img/moment-puppies-hug.webp"
                alt="Volunteer holding two rescue puppies"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-ivi-slate/90 via-ivi-slate/25 to-ivi-slate/10" />
              <div className="absolute inset-x-7 bottom-7 text-white">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] ring-1 ring-white/25 backdrop-blur">
                  <Sparkles className="h-3.5 w-3.5 text-ivi-lime" /> 32,643 volunteers before you
                </span>
                <p className="mt-4 font-heading text-[26px] font-extrabold leading-tight">
                  Two rescues, one very happy volunteer.
                </p>
                <p className="mt-2 text-[14px] leading-relaxed text-white/80">
                  Tell us what moves you and we will match you with a project that needs exactly
                  that.
                </p>
              </div>
              <img
                src="/assets/img/moment-turtle-hands.webp"
                alt="Turtle health check"
                className="absolute right-6 top-6 h-28 w-24 rounded-2xl object-cover ring-4 ring-white/80"
              />
            </div>

            {/* Form side */}
            <div className="relative bg-white p-7 sm:p-9">
              <button
                type="button"
                aria-label="Close form"
                onClick={() => close(false)}
                className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full bg-ivi-sand text-ivi-slate transition-colors duration-200 hover:bg-ivi-mint"
              >
                <X className="h-4 w-4" />
              </button>

              {done ? (
                <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-ivi-mint text-ivi-greenDark">
                    <Check className="h-8 w-8" strokeWidth={3} />
                  </span>
                  <h3 className="mt-6 font-heading text-[26px] font-extrabold text-ivi-slate">
                    You are on your way
                  </h3>
                  <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-ivi-slateSoft">
                    We have your details for{" "}
                    <strong className="font-semibold text-ivi-slate">{form.type}</strong> in{" "}
                    <strong className="font-semibold text-ivi-slate">{form.location}</strong>. A
                    coordinator will email you within one business day.
                  </p>
                  <button
                    type="button"
                    onClick={() => close(false)}
                    className="mt-8 rounded-full bg-ivi-green px-7 py-3.5 font-heading text-[15px] font-bold text-white transition-colors duration-200 hover:bg-ivi-greenDark"
                  >
                    Back to the site
                  </button>
                </div>
              ) : (
                <>
                  <p
                    className="font-heading text-[12px] font-bold uppercase tracking-[0.18em]"
                    style={{ color: RED }}
                  >
                    Start your journey
                  </p>
                  <h3 className="mt-3 font-heading text-[27px] font-extrabold leading-tight text-ivi-slate">
                    Find your project in 30 seconds
                  </h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-ivi-slateSoft">
                    No obligation, no pressure. Just a real human reply from our team.
                  </p>

                  <form onSubmit={submit} className="mt-7 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="First name">
                        <input
                          value={form.name}
                          onChange={(e) => set("name")(e.target.value)}
                          placeholder="Alex"
                          className="h-12 w-full rounded-xl border border-ivi-mintDeep bg-ivi-sand px-4 text-[15px] text-ivi-slate outline-none transition-[border-color,box-shadow] duration-200 focus:border-ivi-green focus:bg-white focus:ring-2 focus:ring-ivi-green/20"
                        />
                      </Field>
                      <Field label="Email">
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => set("email")(e.target.value)}
                          placeholder="you@email.com"
                          className="h-12 w-full rounded-xl border border-ivi-mintDeep bg-ivi-sand px-4 text-[15px] text-ivi-slate outline-none transition-[border-color,box-shadow] duration-200 focus:border-ivi-green focus:bg-white focus:ring-2 focus:ring-ivi-green/20"
                        />
                      </Field>
                    </div>

                    <Field label="Type of volunteering">
                      <Select value={form.type} onValueChange={set("type")}>
                        <SelectTrigger className="h-12 rounded-xl border-ivi-mintDeep bg-ivi-sand text-[15px] text-ivi-slate focus:ring-ivi-green/25">
                          <SelectValue placeholder="Choose what you would like to do" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {TYPES.map((t) => (
                            <SelectItem key={t} value={t} className="text-[14px]">
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="Preferred location">
                      <Select value={form.location} onValueChange={set("location")}>
                        <SelectTrigger className="h-12 rounded-xl border-ivi-mintDeep bg-ivi-sand text-[15px] text-ivi-slate focus:ring-ivi-green/25">
                          <SelectValue placeholder="Choose a country or region" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {LOCATIONS.map((l) => (
                            <SelectItem key={l} value={l} className="text-[14px]">
                              <span className="flex items-center gap-2">
                                <MapPin className="h-3.5 w-3.5 text-ivi-green" />
                                {l}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="How long do you have?" optional>
                      <Select value={form.duration} onValueChange={set("duration")}>
                        <SelectTrigger className="h-12 rounded-xl border-ivi-mintDeep bg-ivi-sand text-[15px] text-ivi-slate focus:ring-ivi-green/25">
                          <SelectValue placeholder="Pick a rough length" />
                        </SelectTrigger>
                        <SelectContent>
                          {DURATIONS.map((d) => (
                            <SelectItem key={d} value={d} className="text-[14px]">
                              {d}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="Anything we should know?" optional>
                      <textarea
                        rows={3}
                        value={form.message}
                        onChange={(e) => set("message")(e.target.value)}
                        placeholder="Travel dates, skills, questions..."
                        className="w-full resize-none rounded-xl border border-ivi-mintDeep bg-ivi-sand px-4 py-3 text-[15px] text-ivi-slate outline-none transition-[border-color,box-shadow] duration-200 focus:border-ivi-green focus:bg-white focus:ring-2 focus:ring-ivi-green/20"
                      />
                    </Field>

                    <button
                      type="submit"
                      style={{ backgroundColor: RED }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = RED_DARK)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = RED)}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-4 font-heading text-[15px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
                    >
                      <Send className="h-4 w-4" /> Send my enquiry
                    </button>
                    <p className="text-center text-[12px] text-ivi-slateSoft">
                      We reply within one business day. Your details stay with us.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, optional, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-2 font-heading text-[12px] font-bold uppercase tracking-[0.14em] text-ivi-slateSoft">
        {label}
        {optional && <span className="font-sans text-[11px] normal-case tracking-normal">optional</span>}
      </span>
      {children}
    </label>
  );
}
